/**
 * <slider-menu> — a reusable off-canvas mobile menu web component.
 *
 * Animations:
 *   1. The drawer slides in from the left when opened (out to the left on close).
 *   2. Tapping an item with a sub-menu slides the sub-menu in from the right,
 *      while the parent panel eases slightly to the left.
 *   3. Pressing back slides the current sub-menu out to the right, revealing
 *      the parent panel underneath.
 *
 * All styles are encapsulated in the shadow DOM, so the component is a
 * drop-in: include this file, add a <slider-menu> element, give it items.
 *
 * Usage (data-driven, recommended):
 *
 *   const menu = document.querySelector('slider-menu');
 *   menu.items = [
 *     { label: 'Veils', href: '/veils' },
 *     { label: 'Wedding Dresses', children: [
 *       { label: 'Shop All Wedding Dresses', href: '/dresses' },
 *       { label: 'Shop by Occasion', children: [ ... ] },
 *     ] },
 *   ];
 *   menu.open();
 *
 * Or — recommended for server-rendered markup — point at a <template> of
 * nested lists. Each <li> is an item; its first <a>/<button>/<span> supplies
 * the label (and href), and a nested <ul> makes it a sub-menu:
 *
 *   <template id="main-menu">
 *     <ul>
 *       <li><a href="/shop">Shop All</a></li>
 *       <li>
 *         <a href="/dresses">Wedding Dresses</a>
 *         <ul>
 *           <li><a href="/dresses">Shop All Wedding Dresses</a></li>
 *           <li><a href="/occasion">Shop by Occasion</a><ul> ... </ul></li>
 *         </ul>
 *       </li>
 *     </ul>
 *   </template>
 *   <slider-menu template="#main-menu"></slider-menu>
 *
 * The <template> may also be a child of the element, or you can drop the raw
 * <ul> in as a child. Inline JSON (<script type="application/json">) and the
 * `items` property are still supported.
 *
 * A persistent footer (e.g. a call-to-action button) can be projected via the
 * "footer" slot — it stays put across every panel:
 *
 *   <slider-menu>
 *     <button slot="footer">Book Appointment</button>
 *   </slider-menu>
 *
 * Attributes:
 *   open                 reflects/controls open state
 *   heading="Menu"       accessible label for the dialog
 *
 * Events (all bubble):
 *   slider-menu:open     fired when the menu opens
 *   slider-menu:close    fired when the menu closes
 *   slider-menu:select   fired when a leaf item is clicked (detail: { item })
 *
 * Theming — override CSS custom properties on the element:
 *   --sm-width   drawer width                (default min(84vw, 380px))
 *   --sm-top     offset from the top, e.g.   (default 0px)
 *                the height of a fixed site header
 *   --sm-bg, --sm-color, --sm-muted, --sm-border, --sm-overlay,
 *   --sm-duration, --sm-ease, --sm-z
 */
class SliderMenu extends HTMLElement {
  static get observedAttributes() {
    return ['open', 'heading', 'template'];
  }

  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    // One DOM panel per level the user has drilled into (root included).
    // _stack mirrors the drilled-into items, deepest last.
    this._stack = [];
    this._items = [];
    this._onKeydown = this._onKeydown.bind(this);
  }

  connectedCallback() {
    // Build the item model from declarative markup unless one was already
    // supplied via the `items` property.
    if (!this._items.length) {
      this._items = this._loadItemsFromDOM() || [];
    }
    this._render();
  }

  disconnectedCallback() {
    document.removeEventListener('keydown', this._onKeydown);
    if (this.isOpen) this._lockScroll(false); // don't leave the page locked
  }

  /* ----- declarative data loading ----- */

  /** Resolve the item model from markup, in order of preference. */
  _loadItemsFromDOM() {
    // 1. template="#selector" pointing at a <template> (or any element) of lists.
    const selector = this.getAttribute('template');
    if (selector) {
      const el = document.querySelector(selector);
      if (el) return this._parseSource(el);
      console.warn(`<slider-menu>: template "${selector}" not found`);
    }
    // 2. A <template> child.
    const childTemplate = this.querySelector('template');
    if (childTemplate) return this._parseSource(childTemplate);
    // 3. Inline JSON.
    const json = this.querySelector('script[type="application/json"]');
    if (json) {
      try {
        return JSON.parse(json.textContent);
      } catch (e) {
        console.warn('<slider-menu>: invalid JSON in inline data', e);
      }
    }
    // 4. A raw <ul> child.
    const ul = this.querySelector('ul');
    if (ul) return this._parseList(ul);
    return null;
  }

  /** Parse a <template> (via its .content) or a plain element holding a <ul>. */
  _parseSource(el) {
    const root = el.content || el;
    const ul = root.querySelector('ul');
    return ul ? this._parseList(ul) : [];
  }

  /** Turn a nested <ul>/<li> tree into the { label, href, children } model. */
  _parseList(ul) {
    const items = [];
    ul.querySelectorAll(':scope > li').forEach((li) => {
      const labelEl = li.querySelector(':scope > a, :scope > button, :scope > span');
      const childUl = li.querySelector(':scope > ul');
      const item = {};
      if (labelEl) {
        item.label = labelEl.textContent.trim();
        const href = labelEl.getAttribute('href');
        if (href) item.href = href;
      } else {
        // Fall back to the text directly inside the <li>.
        item.label = Array.from(li.childNodes)
          .filter((n) => n.nodeType === Node.TEXT_NODE)
          .map((n) => n.textContent)
          .join(' ')
          .trim();
      }
      if (childUl) item.children = this._parseList(childUl);
      items.push(item);
    });
    return items;
  }

  attributeChangedCallback(name) {
    if (!this.shadowRoot.firstChild) return;
    if (name === 'open') {
      this.isOpen ? this._handleOpen() : this._handleClose();
    } else if (name === 'heading') {
      const drawer = this.shadowRoot.querySelector('.drawer');
      if (drawer) drawer.setAttribute('aria-label', this.getAttribute('heading') || 'Menu');
    } else if (name === 'template') {
      this._items = this._loadItemsFromDOM() || [];
      this._resetToRoot();
    }
  }

  /* ----- public API ----- */

  set items(value) {
    this._items = Array.isArray(value) ? value : [];
    if (this.shadowRoot.firstChild) this._resetToRoot();
  }

  get items() {
    return this._items;
  }

  get isOpen() {
    return this.hasAttribute('open');
  }

  open() {
    this.setAttribute('open', '');
  }

  close() {
    this.removeAttribute('open');
  }

  toggle() {
    this.isOpen ? this.close() : this.open();
  }

  /* ----- internals ----- */

  _panelsWrap() {
    return this.shadowRoot.querySelector('.panels');
  }

  _handleOpen() {
    clearTimeout(this._resetTimer);
    this._lastFocused = document.activeElement; // to restore on close
    this._lockScroll(true);
    document.addEventListener('keydown', this._onKeydown);
    this._promote(this.shadowRoot.querySelector('.drawer'));
    this._focusFirst();
    this.dispatchEvent(new CustomEvent('slider-menu:open', { bubbles: true }));
  }

  _handleClose() {
    document.removeEventListener('keydown', this._onKeydown);
    this._lockScroll(false);
    this._promote(this.shadowRoot.querySelector('.drawer'));
    // Return focus to whatever opened the menu.
    if (this._lastFocused && typeof this._lastFocused.focus === 'function') {
      this._lastFocused.focus();
    }
    // After the drawer has slid out, collapse any drilled-in panels so the
    // menu reopens at the root.
    this._resetTimer = setTimeout(() => this._resetToRoot(), this._durationMs() + 40);
    this.dispatchEvent(new CustomEvent('slider-menu:close', { bubbles: true }));
  }

  _onKeydown(e) {
    if (e.key === 'Tab') {
      this._trapTab(e);
      return;
    }
    if (e.key !== 'Escape') return;
    if (this._stack.length) {
      this._back();
    } else {
      this.close();
    }
  }

  /* --- Animation 2: drill in, sub-menu slides from the right --- */
  _drill(item) {
    if (!Array.isArray(item.children) || !item.children.length) return;
    clearTimeout(this._resetTimer);

    const wrap = this._panelsWrap();
    const parent = wrap.lastElementChild; // currently-active panel

    const panel = this._buildPanel(item.children, this._stack.length + 1, item.label, true);
    wrap.appendChild(panel); // starts off-screen to the right (translateX(100%))
    this._stack.push(item);

    // Force a reflow so the freshly-appended panel animates from its
    // off-screen position rather than snapping into place.
    void panel.offsetWidth;

    parent.classList.remove('is-active');
    parent.classList.add('is-past'); // parent eases left
    panel.classList.add('is-active'); // sub-menu slides in from the right

    this._setPanelInert(parent, true); // parent leaves the tab order / AT
    this._setPanelInert(panel, false);
    this._promote(parent);
    this._promote(panel);
    this._focusFirst(); // move focus into the new panel (its back button)
  }

  /* --- Animation 3: back, current sub-menu slides out to the right --- */
  _back() {
    if (!this._stack.length) return;
    clearTimeout(this._resetTimer);

    const wrap = this._panelsWrap();
    const leaving = wrap.lastElementChild;
    const parent = leaving.previousElementSibling;

    this._stack.pop();
    leaving.classList.remove('is-active'); // slides back out to the right
    this._setPanelInert(leaving, true);
    if (parent) {
      parent.classList.remove('is-past'); // parent returns to centre
      parent.classList.add('is-active');
      this._setPanelInert(parent, false);
      this._promote(parent);
    }
    this._focusFirst();
    this._removeAfterTransition(leaving);
  }

  /** Drop a leaving panel once its slide-out transition ends (with fallback).
   *  transitionend bubbles, so ignore events from descendants (e.g. an item's
   *  background transition) and react only to this panel's own transform. */
  _removeAfterTransition(panel) {
    let done = false;
    const finish = (e) => {
      if (e && (e.target !== panel || e.propertyName !== 'transform')) return;
      if (done) return;
      done = true;
      panel.removeEventListener('transitionend', finish);
      panel.remove();
    };
    panel.addEventListener('transitionend', finish);
    // Fallback in case transitionend doesn't fire (e.g. display change).
    setTimeout(finish, this._durationMs() + 80);
  }

  /* ----- click delegation (one handler for the whole panel stack) ----- */

  _onPanelsClick(e) {
    const back = e.target.closest('[data-back]');
    if (back) {
      this._back();
      return;
    }
    const drill = e.target.closest('[data-drill]');
    if (drill) {
      const panel = drill.closest('.panel');
      this._drill(panel._items[Number(drill.dataset.drill)]);
      return;
    }
    const select = e.target.closest('[data-select]');
    if (select) {
      const panel = select.closest('.panel');
      const item = panel._items[Number(select.dataset.select)];
      this.dispatchEvent(
        new CustomEvent('slider-menu:select', { bubbles: true, detail: { item } })
      );
    }
  }

  /* ----- timing / compositor helpers ----- */

  /** The transition duration in ms, read from the (themeable) custom property
   *  so JS timers can't drift from the CSS. */
  _durationMs() {
    const raw = getComputedStyle(this).getPropertyValue('--sm-duration').trim();
    if (raw.endsWith('ms')) return parseFloat(raw) || 320;
    if (raw.endsWith('s')) return (parseFloat(raw) || 0.32) * 1000;
    return 320;
  }

  /** Hint the compositor only while an element is actually sliding. */
  _promote(el) {
    if (!el) return;
    el.style.willChange = 'transform';
    let cleared = false;
    const clear = (e) => {
      if (e && (e.target !== el || e.propertyName !== 'transform')) return;
      if (cleared) return;
      cleared = true;
      el.style.willChange = '';
      el.removeEventListener('transitionend', clear);
    };
    el.addEventListener('transitionend', clear);
    setTimeout(clear, this._durationMs() + 80);
  }

  /* ----- accessibility helpers ----- */

  _lockScroll(lock) {
    const body = document.body;
    if (lock) {
      if (this._scrollLocked) return; // already locked — don't recapture
      this._scrollLocked = true;
      this._prevOverflow = body.style.overflow;
      body.style.overflow = 'hidden';
    } else {
      if (!this._scrollLocked) return;
      this._scrollLocked = false;
      body.style.overflow = this._prevOverflow || '';
    }
  }

  /** Take a panel in/out of the tab order and accessibility tree. */
  _setPanelInert(panel, inert) {
    if (!panel) return;
    panel.setAttribute('aria-hidden', inert ? 'true' : 'false');
    panel.querySelectorAll('a[href], button').forEach((el) => {
      if (inert) el.setAttribute('tabindex', '-1');
      else el.removeAttribute('tabindex');
    });
  }

  /** Focusable elements in the active panel, then the slotted footer. */
  _focusables() {
    const sel = 'a[href], button:not([disabled]), input, select, textarea, [tabindex]:not([tabindex="-1"])';
    const active = this._panelsWrap().querySelector('.panel.is-active');
    const list = active ? Array.from(active.querySelectorAll(sel)) : [];
    const slot = this.shadowRoot.querySelector('.drawer__footer slot');
    if (slot) {
      slot.assignedElements().forEach((el) => {
        if (el.matches(sel)) list.push(el);
        el.querySelectorAll(sel).forEach((n) => list.push(n));
      });
    }
    return list;
  }

  /** The genuinely-focused element, looking through our shadow root. */
  _activeEl() {
    return this.shadowRoot.activeElement || document.activeElement;
  }

  /** Focus without letting the browser scroll the off-screen panel into view —
   *  that scroll would otherwise jump the panel into place and mask its slide. */
  _focusNoScroll(el) {
    if (!el) return;
    try {
      el.focus({ preventScroll: true });
    } catch (e) {
      el.focus();
    }
  }

  _focusFirst() {
    const f = this._focusables();
    if (!f.length) return;
    this._focusNoScroll(f[0]);
    // Belt-and-braces for browsers that ignore preventScroll: snap the panels
    // container back so a focus-induced scroll can't hide the slide animation.
    const panels = this._panelsWrap();
    panels.scrollLeft = 0;
    panels.scrollTop = 0;
  }

  /** Keep Tab focus cycling within the open drawer. */
  _trapTab(e) {
    const f = this._focusables();
    if (!f.length) {
      e.preventDefault();
      return;
    }
    const first = f[0];
    const last = f[f.length - 1];
    const cur = this._activeEl();
    if (e.shiftKey && cur === first) {
      e.preventDefault();
      this._focusNoScroll(last);
    } else if (!e.shiftKey && cur === last) {
      e.preventDefault();
      this._focusNoScroll(first);
    }
  }

  /** Tear down any drilled-in panels and rebuild a fresh root panel. */
  _resetToRoot() {
    this._stack = [];
    const wrap = this._panelsWrap();
    wrap.innerHTML = '';
    const root = this._buildPanel(this._items, 0, '', false);
    root.classList.add('is-active');
    this._setPanelInert(root, false);
    wrap.appendChild(root);
  }

  /* ----- DOM building ----- */

  _buildPanel(items, level, title, showBack) {
    const panel = document.createElement('section');
    panel.className = 'panel';
    panel.dataset.level = String(level);
    // Remember the items this panel renders so click handlers can resolve them.
    panel._items = items || [];

    // The root panel has no header; sub-panels show a back row with the title.
    if (showBack) {
      const bar = document.createElement('button');
      bar.className = 'panel__bar';
      bar.type = 'button';
      bar.dataset.back = '';
      bar.setAttribute('aria-label', 'Back to previous menu');
      // SVG via innerHTML is fine (static markup); the title is set as text.
      bar.innerHTML =
        '<svg class="back-chev" viewBox="0 0 24 24" aria-hidden="true">' +
        '<path d="M15 5l-7 7 7 7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>' +
        '<span class="panel__title"></span>';
      bar.querySelector('.panel__title').textContent = title;
      panel.appendChild(bar);
    }

    const nav = document.createElement('nav');
    nav.className = 'panel__nav';
    const ul = document.createElement('ul');
    (items || []).forEach((item, index) => ul.appendChild(this._buildItem(item, index)));
    nav.appendChild(ul);
    panel.appendChild(nav);
    return panel;
  }

  /** Build one menu row. Labels go in via textContent so markup in a label
   *  (or untrusted data) can't inject HTML. */
  _buildItem(item, index) {
    const li = document.createElement('li');
    const hasChildren = Array.isArray(item.children) && item.children.length;

    if (hasChildren) {
      const btn = document.createElement('button');
      btn.className = 'item item--parent';
      btn.type = 'button';
      btn.dataset.drill = String(index);
      btn.appendChild(this._labelSpan(item.label));
      btn.insertAdjacentHTML(
        'beforeend',
        '<svg class="chev" viewBox="0 0 24 24" aria-hidden="true">' +
          '<path d="M9 5l7 7-7 7" fill="none" stroke="currentColor" stroke-width="1.5"/></svg>'
      );
      li.appendChild(btn);
    } else {
      const a = document.createElement('a');
      a.className = 'item';
      a.href = item.href || '#';
      a.dataset.select = String(index);
      a.appendChild(this._labelSpan(item.label));
      li.appendChild(a);
    }
    return li;
  }

  _labelSpan(label) {
    const span = document.createElement('span');
    span.textContent = label;
    return span;
  }

  _render() {
    this.shadowRoot.innerHTML = `
      <style>
        :host {
          --sm-width: min(84vw, 380px);
          --sm-top: 0px;
          --sm-bg: #ffffff;
          --sm-color: #1b2a38;
          --sm-muted: #9aa6b1;
          --sm-border: #eceef0;
          --sm-overlay: rgba(20, 30, 40, 0.28);
          --sm-duration: 320ms;
          --sm-ease: cubic-bezier(0.22, 0.61, 0.36, 1);
          --sm-z: 1000;
          font-family: inherit;
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: var(--sm-overlay);
          opacity: 0;
          visibility: hidden;
          transition: opacity var(--sm-duration) var(--sm-ease),
                      visibility var(--sm-duration) var(--sm-ease);
          z-index: var(--sm-z);
        }

        /* Animation 1: drawer slides in from the left. Sits below --sm-top
           so a fixed site header above it stays visible and clickable. */
        .drawer {
          position: fixed;
          top: var(--sm-top);
          left: 0;
          height: calc(100% - var(--sm-top));
          width: var(--sm-width);
          display: flex;
          flex-direction: column;
          background: var(--sm-bg);
          color: var(--sm-color);
          transform: translateX(-100%);
          /* visibility flips to hidden only after the slide-out completes, so a
             closed drawer is out of the tab order and accessibility tree. */
          visibility: hidden;
          transition: transform var(--sm-duration) var(--sm-ease),
                      visibility 0s linear var(--sm-duration);
          z-index: calc(var(--sm-z) + 1);
          overflow: hidden;
          box-shadow: 0 0 32px rgba(0, 0, 0, 0.10);
        }

        :host([open]) .overlay {
          opacity: 1;
          visibility: visible;
        }
        :host([open]) .drawer {
          transform: translateX(0);
          visibility: visible;
          transition: transform var(--sm-duration) var(--sm-ease),
                      visibility 0s;
        }

        .panels {
          position: relative;
          flex: 1 1 auto;
          overflow: hidden;
        }

        /* Panels stack; each inactive sub-panel waits off-screen to the right.
           Animations 2 & 3 are driven by toggling .is-active / .is-past. */
        .panel {
          position: absolute;
          inset: 0;
          display: flex;
          flex-direction: column;
          background: var(--sm-bg);
          transform: translateX(100%);
          transition: transform var(--sm-duration) var(--sm-ease);
        }
        .panel[data-level="0"] {
          transform: translateX(0); /* root is the base layer */
        }
        .panel.is-active {
          transform: translateX(0);
        }
        .panel.is-past {
          transform: translateX(-26%); /* parent eases left as child enters */
        }

        /* Sub-panel back row: thin chevron + bold title. */
        .panel__bar {
          display: flex;
          align-items: center;
          gap: 6px;
          width: 100%;
          box-sizing: border-box;
          padding: 18px 22px 14px;
          margin: 0;
          border: 0;
          background: transparent;
          color: inherit;
          font-family: inherit;
          cursor: pointer;
          text-align: left;
        }
        .back-chev {
          width: 16px;
          height: 16px;
          flex: none;
        }
        .panel__title {
          font-size: 13px;
          font-weight: 700;
          letter-spacing: 0.12em;
          text-transform: uppercase;
        }

        .panel__nav {
          flex: 1;
          overflow-y: auto;
          -webkit-overflow-scrolling: touch;
          padding: 6px 0 24px;
        }
        .panel__nav ul {
          list-style: none;
          margin: 0;
          padding: 0;
        }

        /* Borderless, uppercase, letter-spaced rows. */
        .item {
          display: flex;
          align-items: center;
          justify-content: space-between;
          gap: 12px;
          width: 100%;
          box-sizing: border-box;
          padding: 13px 22px;
          font-size: 13px;
          font-weight: 400;
          letter-spacing: 0.1em;
          text-transform: uppercase;
          color: inherit;
          text-decoration: none;
          background: transparent;
          border: 0;
          cursor: pointer;
          text-align: left;
          font-family: inherit;
          transition: background var(--sm-duration) var(--sm-ease);
        }
        .item:hover {
          background: rgba(20, 30, 40, 0.04);
        }
        .chev {
          width: 16px;
          height: 16px;
          flex: none;
          color: var(--sm-muted);
        }

        /* Close button, pinned to the top-right corner of the drawer. */
        .drawer__close {
          position: absolute;
          top: 14px;
          right: 14px;
          z-index: 2;
          width: 40px;
          height: 40px;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0;
          margin: 0;
          border: 0;
          background: transparent;
          color: inherit;
          cursor: pointer;
        }

        /* Persistent header (slotted logo), stays above every panel. */
        .drawer__header {
          flex: none;
          padding: 18px 22px;
          display: flex;
          justify-content: flex-start;
        }
        .drawer__header.is-empty {
          display: none;
        }
        ::slotted([slot="header"]) {
          display: block;
        }

        /* Persistent footer (slotted CTA), stays across every panel. */
        .drawer__footer {
          flex: none;
          padding: 16px 22px 24px;
        }
        .drawer__footer.is-empty {
          display: none;
        }
        ::slotted([slot="footer"]) {
          display: block;
          width: 100%;
        }

        /* Honour reduced-motion by easing the motion off — shorter slides and
           no parallax nudge — rather than removing the slide entirely, which
           is the orienting cue this menu relies on. */
        @media (prefers-reduced-motion: reduce) {
          :host {
            --sm-duration: 160ms;
          }
          .panel.is-past {
            transform: translateX(0);
          }
        }
      </style>

      <div class="overlay" part="overlay"></div>
      <aside class="drawer" part="drawer" role="dialog" aria-modal="true"
             aria-label="${this.getAttribute('heading') || 'Menu'}">
        <button class="drawer__close" type="button" part="close" aria-label="Close menu">
          <svg viewBox="0 0 15 15" width="15" height="15" aria-hidden="true">
            <path d="M11.875 3.125L3.125 11.875" stroke="currentColor"/>
            <path d="M3.125 3.125L11.875 11.875" stroke="currentColor"/>
          </svg>
        </button>
        <div class="drawer__header is-empty" part="header">
          <slot name="header"></slot>
        </div>
        <div class="panels"></div>
        <div class="drawer__footer is-empty" part="footer">
          <slot name="footer"></slot>
        </div>
      </aside>
    `;

    this.shadowRoot
      .querySelector('.overlay')
      .addEventListener('click', () => this.close());

    this.shadowRoot
      .querySelector('.drawer__close')
      .addEventListener('click', () => this.close());

    // One delegated handler for every drill / select / back across all panels.
    this.shadowRoot
      .querySelector('.panels')
      .addEventListener('click', (e) => this._onPanelsClick(e));

    // Hide a slotted area (header / footer) whenever nothing is projected into it.
    const wireSlot = (sel) => {
      const box = this.shadowRoot.querySelector(sel);
      const slot = box.querySelector('slot');
      const sync = () =>
        box.classList.toggle('is-empty', slot.assignedNodes().length === 0);
      slot.addEventListener('slotchange', sync);
      sync();
    };
    wireSlot('.drawer__header');
    wireSlot('.drawer__footer');

    this._resetToRoot();
  }
}

customElements.define('slider-menu', SliderMenu);
